import { Row } from "PFApp/booking/types";
import { EditMode } from "PFApp/booking/types/edit_mode";
import { UNDETERMINED_FUTURE } from "PFApp/constants/dates";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { usePastDaysLimit } from "PFApp/hooks";
import { useBookingsForUpdate } from "PFCore/hooks/queries/bookings/bulk_operation/use_bookings_for_update";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { BookingsBulkMovePayload, BulkMoveFilter, Exclude } from "PFCore/services/bookings_bulk_move";
import { Id } from "PFTypes";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useBookingsOverviewContext } from "../../bookings_overview_context";
import { BookingRowsContextType } from "../booking_rows_context";
import { UseEditModeType } from "../use_edit_mode";

export type SelectModeData = {
  row?: Row;
} & Pick<BookingsBulkMovePayload, "filters" | "exclude">;

export type UseSelectModeType = {
  changeSelection: (data: SelectModeData) => void;
  toggleBookingSelection: (bookingId: Id) => void;
  shouldBookingBeSelected: (bookingId: Id, row: Row) => boolean;
  isSelectable: (bookingId: Id, row: Row) => boolean;
  selection: SelectModeData | null;
  bookingsCount?: number;
  bookingsRangeMinDate?: string;
  bookingsRangeMaxDate?: string;
};

type UseSelectMode = {
  bookingsByRowKey: BookingRowsContextType["bookingsByRowKey"];
  editMode: UseEditModeType["editMode"];
};

export const useSelectMode = ({ bookingsByRowKey, editMode }: UseSelectMode): UseSelectModeType => {
  const [selection, setSelection] = useState<SelectModeData | null>(null);
  const { calendarMode } = useBookingsOverviewContext();
  const pastDaysLimit = usePastDaysLimit();
  const { utc, formatISODate } = useDateFormatter();

  const preparePayload = (filters: BulkMoveFilter[], exclude?: Exclude) => ({
    filters,
    ...(exclude ? { exclude } : {}),
    dateRange: {
      min: formatISODate(utc().subtract(pastDaysLimit, "days")),
      max: formatISODate(UNDETERMINED_FUTURE)
    },
    page: 1,
    perPage: PER_PAGE_UNPAGINATED
  });

  const { data: bookingsForUpdateData } = useBookingsForUpdate(
    "filtered_selection",
    preparePayload(selection?.filters ?? [], selection?.exclude),
    {
      enabled: !!selection,
      keepPreviousData: true,
      cacheTime: 0
    }
  );

  const { data: selectableBookingsData } = useBookingsForUpdate(
    "total_selection",
    preparePayload(selection?.filters ?? []),
    {
      enabled: !!selection?.filters,
      keepPreviousData: true,
      cacheTime: 0
    }
  );

  const selectableBookingIds =
    editMode === EditMode.Select ? selectableBookingsData?.entries.map(({ id }) => id) : undefined;

  const bookingsTotal = bookingsForUpdateData?.meta.total;

  const bookingsCount = useMemo(
    () => (editMode === EditMode.Select ? bookingsTotal : undefined),
    [editMode, bookingsTotal]
  );

  const minDateRange = bookingsForUpdateData?.meta.dateRange?.min;
  const bookingsRangeMinDate = useMemo(
    () => (editMode === EditMode.Select ? minDateRange : undefined),
    [editMode, minDateRange]
  );

  const maxDateRange = bookingsForUpdateData?.meta.dateRange?.max;
  const bookingsRangeMaxDate = useMemo(
    () => (editMode === EditMode.Select ? maxDateRange : undefined),
    [editMode, maxDateRange]
  );

  useEffect(() => {
    if (editMode !== EditMode.Select) {
      setSelection(null);
    }
  }, [editMode]);

  const changeSelection = useCallback(
    ({ filters, exclude, row }: SelectModeData) => {
      const updatedSelection = {
        ...(selection ?? {}),
        filters,
        row,
        exclude
      };

      setSelection(updatedSelection);
    },
    [selection, bookingsByRowKey, calendarMode]
  );

  const toggleBookingSelection = useCallback(
    (bookingId: Id) => {
      if (selection) {
        const excludedIds = selection.exclude?.id ?? [];
        const isExcluded = excludedIds.includes(bookingId) ?? false;

        const newExcludedIds = isExcluded
          ? excludedIds.filter((id) => id !== bookingId)
          : [...excludedIds, bookingId];

        const exclude = {
          ...selection.exclude,
          id: newExcludedIds.length > 0 ? newExcludedIds : undefined
        };

        const updatedSelection = {
          ...selection,
          exclude
        };

        setSelection(updatedSelection);
      }
    },
    [selection, bookingsByRowKey, calendarMode]
  );

  const isChildRow = useCallback(
    (row: Row) =>
      selection && selection.row
        ? row.parents.includes(selection.row.key) || row.key === selection.row.key
        : false,
    [selection]
  );

  const shouldBeSelected = useCallback(
    (bookingId: Id, row: Row) => {
      if (selection) {
        const isChild = selection?.row ? isChildRow(row) : true;
        const existsInSelectedBookings = bookingsForUpdateData?.entries.some(({ id }) => id === bookingId);
        return isChild && !!existsInSelectedBookings;
      }
      return false;
    },
    [selection, bookingsForUpdateData]
  );

  const isSelectable = useCallback(
    (bookingId: Id, row: Row) => {
      if (selection) {
        const isChild = selection?.row ? isChildRow(row) : true;
        return isChild && (selectableBookingIds?.includes(bookingId) ?? false);
      }
      return false;
    },
    [selection, selectableBookingIds]
  );

  return {
    changeSelection,
    toggleBookingSelection: toggleBookingSelection,
    selection,
    shouldBookingBeSelected: shouldBeSelected,
    isSelectable,
    bookingsCount,
    bookingsRangeMinDate,
    bookingsRangeMaxDate
  };
};
